/** The quick actions every value on an import-run surface offers — the
 *  detail dialog's header and Info tab, plus the per-source and child rows.
 *
 *  The dialog is the grid's detail view, so the sets here mirror the grid's
 *  own cell popovers (`importRunsColumns.tsx`) and add the cross-grid hops a
 *  full record makes possible: a request id reaches the webhook that started
 *  the run, a source code reaches every other run that processed it.
 *
 *  Every href comes from gridLinks; every action from ValueActions' `act`.
 *
 *  Id-space note: `Import_Run.task_id` is the CELERY task id, not a
 *  BackgroundTaskRecord id — it filters `/import-runs?q=task=` and NEVER
 *  links to `/bgtasks/<id>` (see the plan's "Never introduce" list).
 */
import { act, type ValueAction } from '@/components/ValueActions/ValueActions';
import {
  apiRequestsHref,
  cond,
  createdAroundNode,
  deliveryLogsHref,
  importRunsHref,
} from '@/utils/gridLinks';

export interface ImportRunCtx {
  /** Scopes the grid links (`?tenant=`). */
  tenantId: number | null | undefined;
}

const tenantOf = (ctx: ImportRunCtx) => ctx.tenantId ?? null;

// ---- Identity -------------------------------------------------------------

export function tenantActions(tenant: { id: number }): ValueAction[] {
  return [
    act.open(`/tour-operators/${tenant.id}`, 'Open operator'),
    act.filter(importRunsHref([], tenant.id), 'Import runs of this operator'),
    act.filter(apiRequestsHref([], { tenant: tenant.id }), 'API requests of this operator (7 days)'),
  ];
}

export function statusActions(label: string, ctx: ImportRunCtx): ValueAction[] {
  return [act.filter(importRunsHref([cond('status', label)], tenantOf(ctx)), `Runs with status "${label}"`)];
}

export function originActions(label: string, ctx: ImportRunCtx): ValueAction[] {
  return [act.filter(importRunsHref([cond('origin', label)], tenantOf(ctx)), `Runs started by "${label}"`)];
}

/** The ±5m … ±7d presets on the import-runs `created` column. */
export function createdActions(iso: string, ctx: ImportRunCtx): ValueAction[] {
  const t = tenantOf(ctx);
  return act.timeRanges(iso, (range) => importRunsHref([range], t));
}

/** The run's own UUID. `id` is exact-only on this grid. */
export function importIdActions(id: string, ctx: ImportRunCtx): ValueAction[] {
  return [
    act.copy(id, 'Copy import id'),
    act.filter(importRunsHref([cond('id', id, 'exact')], tenantOf(ctx)), 'This run in the grid'),
  ];
}

/** The group parent's UUID — every run of a preprocessed/parallel import. */
export function groupActions(group: string, ctx: ImportRunCtx): ValueAction[] {
  return [
    act.copy(group, 'Copy group id'),
    act.filter(importRunsHref([cond('group', group, 'exact')], tenantOf(ctx)), 'Every run in this group'),
  ];
}

/** The platform request id that created the run: the webhook landed moments
 *  before it, so the api-requests hop is windowed ±1 h around `createdTs`. */
export function requestIdActions(
  requestId: string,
  createdTs: string | null | undefined,
  ctx: ImportRunCtx,
): ValueAction[] {
  const t = tenantOf(ctx);
  return [
    act.copy(requestId, 'Copy request id'),
    act.filter(importRunsHref([cond('request', requestId, 'exact')], t), 'Import runs from this request'),
    act.filter(
      apiRequestsHref([cond('request', requestId, 'exact')], {
        tenant: t,
        ...(createdTs ? { window: createdAroundNode(createdTs) } : {}),
      }),
      createdTs ? 'API requests with this request id (±1 h)' : 'API requests with this request id (7 days)',
    ),
    act.filter(
      deliveryLogsHref([cond('request', requestId)], {
        tenant: t,
        ...(createdTs ? { window: createdAroundNode(createdTs, 12) } : {}),
      }),
      'Delivery logs from this request',
    ),
  ];
}

/** Celery task id — filters the import grid, never /bgtasks (different id
 *  spaces; the help text on the namespace says so too). */
export function taskIdActions(taskId: string, ctx: ImportRunCtx): ValueAction[] {
  return [
    act.copy(taskId, 'Copy Celery task id'),
    act.filter(importRunsHref([cond('task', taskId, 'exact')], tenantOf(ctx)), 'Runs of this Celery task'),
  ];
}

export function sourceUrlActions(url: string): ValueAction[] {
  const out: ValueAction[] = [act.copy(url, 'Copy source URL')];
  if (/^https?:\/\//i.test(url)) out.push(act.external(url, 'Open the source URL'));
  return out;
}

/** An import SOURCE code (`source=` is an exact index reference). */
export function sourceCodeActions(code: string, ctx: ImportRunCtx): ValueAction[] {
  return [
    act.copy(code, 'Copy source code'),
    act.filter(importRunsHref([cond('source', code, 'exact')], tenantOf(ctx)), `Runs that processed "${code}"`),
  ];
}
